import * as React from 'react';
import {
    Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    createBetupFeedChannel, createBetupFeedChannelCategory, createBetupFeedChannelPost, updateBetupFeedChannel,
    updateBetupFeedChannelCategory, updateBetupFeedChannelPost,
} from '../../Api';
import {
    BETUP_FEED_CHANNEL_LANGUAGES, BetupFeedChannelCategoryDto, BetupFeedChannelCategoryRequestDto, BetupFeedChannelDto,
    BetupFeedChannelLanguage, BetupFeedChannelPostDto, BetupFeedChannelPostRequestDto,
    BetupFeedChannelPostUpdateRequestDto, BetupFeedChannelRequestDto, BetupFeedLocalizedTexts,
} from '../../models/dtos/BetupFeedChannelDto';
import SportEntityDto from '../../models/dtos/SportEntityDto';

export const CHANNEL_NAME_MAX = 80;
export const CHANNEL_DESCRIPTION_MAX = 300;
const CATEGORY_NAME_MAX = 80;
const CATEGORY_CODE_MAX = 32;
const CHANNEL_LOGO_MAX = 512;
const CHANNEL_MODEL_MAX = 64;
/** Per language (docs/feed-post-languages-and-drafts.md §4). */
const POST_TEXT_MAX = 5000;

export const LANGUAGE_LABELS: Record<BetupFeedChannelLanguage, string> = {
    en: 'English', ru: 'Russian', ro: 'Romanian', es: 'Spanish', it: 'Italian', pt: 'Portuguese', de: 'German',
    fr: 'French', tr: 'Turkish', hi: 'Hindi', ar: 'Arabic', uk: 'Ukrainian', bg: 'Bulgarian',
};

const isHttpUrl = (value: string) => /^https?:\/\/\S+$/i.test(value);
const parsePositiveId = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN);

/** The English name (required on the server), else `#id`. */
export const channelName = (channel: Pick<BetupFeedChannelDto, 'id' | 'names'>) => channel.names?.en || `#${channel.id}`;
export const categoryName = (category: BetupFeedChannelCategoryDto) => category.names?.en || category.code;

/** How many languages have a name. */
export const namedLanguagesCount = (texts: BetupFeedLocalizedTexts | null | undefined) =>
    BETUP_FEED_CHANNEL_LANGUAGES.filter(lang => (texts?.[lang] ?? '').trim().length > 0).length;

export const languagesLabel = (count: number) => `${count} language${count === 1 ? '' : 's'}`;

/** `languages` in the canonical order (the server's list of 13), unknown codes dropped. */
const orderedLanguages = (languages: readonly string[] | null | undefined): BetupFeedChannelLanguage[] =>
    BETUP_FEED_CHANNEL_LANGUAGES.filter(lang => (languages ?? []).includes(lang));

/** The languages the channel writes its posts in (empty when not set yet). */
export const channelLanguages = (channel: Pick<BetupFeedChannelDto, 'languages'>) => orderedLanguages(channel.languages);

/** A post's texts; a server without per-language texts only sends `text` (English). */
const postTexts = (post: BetupFeedChannelPostDto | null): BetupFeedLocalizedTexts =>
    post == null ? {} : post.texts ?? (post.text ? { en: post.text } : {});

/** The languages a post has a text in. */
export const postLanguages = (post: BetupFeedChannelPostDto): BetupFeedChannelLanguage[] => {
    const texts = postTexts(post);
    return BETUP_FEED_CHANNEL_LANGUAGES.filter(lang => (texts[lang] ?? '').trim().length > 0);
};

/** Small uppercase code chips, e.g. EN RU. */
export function LanguageChips({ languages }: { languages: BetupFeedChannelLanguage[] }) {
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {languages.map(lang => (
                <Chip key={lang} size="small" variant="outlined" label={lang.toUpperCase()} title={LANGUAGE_LABELS[lang]}
                    sx={{ height: 20, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
            ))}
        </Stack>
    );
}

type TextMap = Record<BetupFeedChannelLanguage, string>;

const textMapOf = (texts: BetupFeedLocalizedTexts | null | undefined): TextMap =>
    Object.fromEntries(BETUP_FEED_CHANNEL_LANGUAGES.map(lang => [lang, texts?.[lang] ?? ''])) as TextMap;

/** Every language, trimmed; blank → null (the server then falls back to English). */
const textsBody = (texts: TextMap): BetupFeedLocalizedTexts =>
    Object.fromEntries(BETUP_FEED_CHANNEL_LANGUAGES.map(lang => [lang, texts[lang].trim() || null])) as BetupFeedLocalizedTexts;

const textsEqual = (a: TextMap, b: TextMap) => BETUP_FEED_CHANNEL_LANGUAGES.every(lang => a[lang].trim() === b[lang].trim());

interface LocalizedField {
    label: string;
    max: number;
    multiline?: boolean;
    helper?: string;
    values: TextMap;
    onChange: (lang: BetupFeedChannelLanguage, value: string) => void;
}

/** Tabs per language with one text field per `fields` entry; English is required for the first field. */
function LocalizedTextsEditor({ fields }: { fields: LocalizedField[] }) {
    const [lang, setLang] = React.useState<BetupFeedChannelLanguage>('en');
    const filled = (l: BetupFeedChannelLanguage) => fields.some(f => f.values[l].trim().length > 0);
    const invalid = (l: BetupFeedChannelLanguage) =>
        fields.some(f => f.values[l].trim().length > f.max) || (l === 'en' && fields[0].values.en.trim().length === 0);
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Tabs value={lang} onChange={(_, value) => setLang(value)} variant="scrollable" scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {BETUP_FEED_CHANNEL_LANGUAGES.map(l => (
                    <Tab
                        key={l}
                        value={l}
                        title={LANGUAGE_LABELS[l]}
                        sx={{ minWidth: 56, px: 1.5, color: invalid(l) ? 'error.main' : filled(l) ? 'text.primary' : 'text.disabled' }}
                        label={`${l.toUpperCase()}${l === 'en' ? ' *' : filled(l) ? ' ✓' : ''}`}
                    />
                ))}
            </Tabs>
            <Stack gap={2} sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {LANGUAGE_LABELS[lang]}{lang === 'en'
                        ? ' — required; the other languages fall back to it.'
                        : ' — optional; players in this language see English when it is empty.'}
                </Typography>
                {fields.map((field, index) => {
                    const value = field.values[lang];
                    const length = value.trim().length;
                    const required = index === 0 && lang === 'en';
                    return (
                        <TextField
                            key={field.label}
                            label={`${field.label} (${lang})`}
                            required={required}
                            fullWidth
                            multiline={field.multiline}
                            minRows={field.multiline ? 2 : undefined}
                            value={value}
                            onChange={e => field.onChange(lang, e.target.value)}
                            inputProps={{ maxLength: field.max, dir: lang === 'ar' ? 'rtl' : undefined }}
                            error={length > field.max}
                            helperText={`${field.helper ? field.helper + ' ' : ''}${length} / ${field.max}`}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
}

/** A URL field's preview: the image, or a note when it doesn't load. */
function ImagePreview({ url, round = false }: { url: string; round?: boolean }) {
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => setFailed(false), [url]);
    if (!url || !isHttpUrl(url)) return null;
    if (failed) return <Typography variant="caption" color="error">The image could not be loaded from this URL.</Typography>;
    return round
        ? <Avatar src={url} alt="logo preview" sx={{ width: 64, height: 64 }} imgProps={{ onError: () => setFailed(true) }} />
        : (
            <Box component="img" src={url} alt="image preview" onError={() => setFailed(true)}
                sx={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 1, display: 'block' }} />
        );
}

export function ChannelLogo({ channel, size = 40 }: { channel: Pick<BetupFeedChannelDto, 'id' | 'names' | 'logo_url'>; size?: number }) {
    const name = channelName(channel);
    return (
        <Avatar src={channel.logo_url ?? undefined} alt={name} sx={{ width: size, height: size }}>
            {(channel.names?.en || '?').slice(0, 1).toUpperCase()}
        </Avatar>
    );
}

/** "Top" (computed by the Crons) and "Pinned" (the operator's override) chips. */
export function TopChannelChips({ channel }: { channel: BetupFeedChannelDto }) {
    if (!channel.is_top && !channel.top_pinned) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {channel.is_top && <Chip size="small" color="warning" label="Top" />}
            {channel.top_pinned && <Chip size="small" color="warning" variant="outlined" label="Pinned" />}
        </Stack>
    );
}

interface ChannelForm {
    languages: BetupFeedChannelLanguage[];
    names: TextMap;
    descriptions: TextMap;
    logoUrl: string;
    categoryIds: number[];
    sportIds: number[];
    active: boolean;
    topPinned: boolean;
    model: string;
    systemPrompt: string;
}

const formOf = (channel: BetupFeedChannelDto | null): ChannelForm => ({
    // A new channel writes in English until more are picked.
    languages: channel == null ? ['en'] : channelLanguages(channel),
    names: textMapOf(channel?.names),
    descriptions: textMapOf(channel?.descriptions),
    logoUrl: channel?.logo_url ?? '',
    categoryIds: channel?.category_ids ?? [],
    sportIds: channel?.sport_ids ?? [],
    active: channel?.active ?? true,
    topPinned: channel?.top_pinned ?? false,
    model: channel?.model ?? '',
    systemPrompt: channel?.system_prompt ?? '',
});

const sameIds = (a: number[], b: number[]) => a.length === b.length && [...a].sort().every((id, i) => id === [...b].sort()[i]);
const sameLanguages = (a: BetupFeedChannelLanguage[], b: BetupFeedChannelLanguage[]) =>
    orderedLanguages(a).join() === orderedLanguages(b).join();

/** Create: blank optional fields are left out. */
function createBody(form: ChannelForm): BetupFeedChannelRequestDto {
    const opt = (value: string) => value.trim() || null;
    return {
        languages: orderedLanguages(form.languages),
        names: textsBody(form.names),
        descriptions: textsBody(form.descriptions),
        logo_url: opt(form.logoUrl),
        system_prompt: opt(form.systemPrompt),
        model: opt(form.model),
        category_ids: form.categoryIds,
        sport_ids: form.sportIds,
        active: form.active,
        top_pinned: form.topPinned,
    };
}

/**
 * Update: only the changed fields (null keeps a value on the server). A cleared text field is sent as ""; a changed
 * names / descriptions map is sent whole (every language, null where empty).
 */
function updateBody(form: ChannelForm, channel: BetupFeedChannelDto): BetupFeedChannelRequestDto {
    const body: BetupFeedChannelRequestDto = {};
    const original = formOf(channel);
    if (!sameLanguages(form.languages, original.languages)) body.languages = orderedLanguages(form.languages);
    if (!textsEqual(form.names, original.names)) body.names = textsBody(form.names);
    if (!textsEqual(form.descriptions, original.descriptions)) body.descriptions = textsBody(form.descriptions);
    if (form.logoUrl.trim() !== original.logoUrl.trim()) body.logo_url = form.logoUrl.trim();
    if (form.model.trim() !== original.model.trim()) body.model = form.model.trim();
    // The prompt is kept as written (no trim) — only compared.
    if (form.systemPrompt !== original.systemPrompt) body.system_prompt = form.systemPrompt;
    if (!sameIds(form.categoryIds, original.categoryIds)) body.category_ids = form.categoryIds;
    if (!sameIds(form.sportIds, original.sportIds)) body.sport_ids = form.sportIds;
    if (form.active !== channel.active) body.active = form.active;
    if (form.topPinned !== channel.top_pinned) body.top_pinned = form.topPinned;
    return body;
}

type IdOption = { id: number; label: string; inactive?: boolean };

/** A multi-select of ids; ids missing from `options` (not loaded, or removed) still show as `#id`. */
function IdsSelect({ label, options, value, onChange, helperText }: {
    label: string;
    options: IdOption[];
    value: number[];
    onChange: (ids: number[]) => void;
    helperText?: string;
}) {
    const byId = new Map(options.map(o => [o.id, o]));
    const selected = value.map(id => byId.get(id) ?? { id, label: `#${id}` });
    return (
        <Autocomplete
            multiple
            options={options}
            value={selected}
            onChange={(_, next) => onChange(next.map(o => o.id))}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={o => (o.inactive ? `${o.label} (inactive)` : o.label)}
            filterSelectedOptions
            renderInput={params => <TextField {...params} label={label} helperText={helperText} />}
        />
    );
}

/** The channel's post languages: a multi-select of the 13 codes, at least one. */
function LanguagesSelect({ value, onChange }: {
    value: BetupFeedChannelLanguage[];
    onChange: (languages: BetupFeedChannelLanguage[]) => void;
}) {
    const empty = value.length === 0;
    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={[...BETUP_FEED_CHANNEL_LANGUAGES]}
            value={orderedLanguages(value)}
            onChange={(_, next) => onChange(orderedLanguages(next))}
            getOptionLabel={lang => `${LANGUAGE_LABELS[lang]} (${lang})`}
            filterSelectedOptions
            renderInput={params => (
                <TextField
                    {...params}
                    label="Languages"
                    required
                    error={empty}
                    helperText={empty
                        ? 'Pick at least one language.'
                        : 'The languages this channel writes its posts in: every post has a text per language, and '
                        + 'players read theirs (else English, else any). Removing a language keeps existing texts.'}
                />
            )}
        />
    );
}

/** Create (channel == null) or edit a channel. Server errors (400: e.g. a duplicate name) stay in the dialog. */
export function ChannelDialog({ open, channel, categories, sports, topMinSubscribers, onClose, onSaved }: {
    open: boolean;
    channel: BetupFeedChannelDto | null;
    categories: BetupFeedChannelCategoryDto[];
    /** null when the sports list failed to load. */
    sports: SportEntityDto[] | null;
    topMinSubscribers: string;
    onClose: () => void;
    onSaved: (saved: BetupFeedChannelDto | null, created: boolean) => void;
}) {
    const [form, setForm] = React.useState<ChannelForm>(formOf(channel));
    const [error, setError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setForm(formOf(channel));
        setError(null);
        setSaving(false);
    }, [open, channel]);

    const set = <K extends keyof ChannelForm>(key: K, value: ChannelForm[K]) => setForm(f => ({ ...f, [key]: value }));
    const setText = (key: 'names' | 'descriptions') => (lang: BetupFeedChannelLanguage, value: string) =>
        setForm(f => ({ ...f, [key]: { ...f[key], [lang]: value } }));
    const logo = form.logoUrl.trim();
    const textsValid = form.names.en.trim().length > 0
        && BETUP_FEED_CHANNEL_LANGUAGES.every(lang =>
            form.names[lang].trim().length <= CHANNEL_NAME_MAX && form.descriptions[lang].trim().length <= CHANNEL_DESCRIPTION_MAX);
    const logoError = logo && !isHttpUrl(logo) ? 'An http(s) URL' : null;
    const changes = channel == null ? null : updateBody(form, channel);
    const unchanged = changes != null && Object.keys(changes).length === 0;
    const valid = textsValid && logoError == null && form.languages.length > 0;

    const categoryOptions: IdOption[] = [...categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(c => ({ id: c.id, label: categoryName(c), inactive: !c.active }));
    const sportOptions: IdOption[] = (sports ?? []).map(s => ({ id: s.id, label: s.name }));

    const save = async () => {
        setSaving(true);
        setError(null);
        const result = channel == null
            ? await createBetupFeedChannel(createBody(form))
            : await updateBetupFeedChannel(channel.id, changes ?? {});
        setSaving(false);
        if (result.error != null) {
            setError(result.error);
            return;
        }
        onSaved(result.data, channel == null);
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{channel == null ? 'New channel' : `Edit channel #${channel.id} — ${channelName(channel)}`}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    <LanguagesSelect value={form.languages} onChange={languages => set('languages', languages)} />
                    <LocalizedTextsEditor fields={[
                        {
                            label: 'Name', max: CHANNEL_NAME_MAX, values: form.names, onChange: setText('names'),
                            helper: 'The English name is unique among channels.',
                        },
                        {
                            label: 'Description', max: CHANNEL_DESCRIPTION_MAX, multiline: true, values: form.descriptions,
                            onChange: setText('descriptions'), helper: 'Shown to players on the channel screen.',
                        },
                    ]} />
                    <Stack direction="row" gap={2} alignItems="flex-start">
                        <TextField
                            label="Logo URL" fullWidth value={form.logoUrl}
                            onChange={e => set('logoUrl', e.target.value)}
                            inputProps={{ maxLength: CHANNEL_LOGO_MAX }}
                            error={logoError != null}
                            helperText={logoError ?? 'A square image; players see it round.'}
                        />
                        <Box sx={{ minWidth: 64 }}><ImagePreview url={logo} round /></Box>
                    </Stack>
                    <IdsSelect
                        label="Categories" options={categoryOptions} value={form.categoryIds}
                        onChange={ids => set('categoryIds', ids)}
                        helperText="Badges on the channel screen; players can filter channels by category."
                    />
                    <IdsSelect
                        label="Sports" options={sportOptions} value={form.sportIds}
                        onChange={ids => set('sportIds', ids)}
                        helperText={sports == null
                            ? 'The sports list could not be loaded — only the current ids are shown.'
                            : 'Shown on the channel screen; players can filter channels by sport.'}
                    />
                    <Stack>
                        <FormControlLabel
                            control={<Switch checked={form.active} onChange={e => set('active', e.target.checked)} />}
                            label="Active"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 6 }}>
                            An inactive channel is not listed or searchable, and its posts leave the feeds.
                        </Typography>
                    </Stack>
                    <Stack>
                        <FormControlLabel
                            control={<Switch checked={form.topPinned} onChange={e => set('topPinned', e.target.checked)} />}
                            label="Pin as top channel"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 6 }}>
                            A channel is top when it is pinned or has more than {topMinSubscribers} subscribers. Top channels
                            are listed first and feed the players' “Most popular channels” section. The Crons recount
                            subscribers and recompute “top” every 30 minutes, so a change shows up there within half an
                            hour; a pin survives the recount, unpinning lets the count decide again.
                        </Typography>
                    </Stack>
                    <Alert severity="info" variant="outlined">
                        Model and system prompt are never shown to players. An agent service reads them through the MCP
                        server (get_channel) to write this channel's posts — usually as drafts you publish from Posts —
                        and posts can still be written by hand (Posts › New post).
                    </Alert>
                    <TextField
                        label="Model" fullWidth value={form.model}
                        onChange={e => set('model', e.target.value)}
                        inputProps={{ maxLength: CHANNEL_MODEL_MAX }}
                        helperText="e.g. claude-haiku-4-5. Hidden from players."
                    />
                    <TextField
                        label="System prompt" fullWidth multiline minRows={6} maxRows={20} value={form.systemPrompt}
                        onChange={e => set('systemPrompt', e.target.value)}
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        helperText={`Hidden from players. ${form.systemPrompt.length.toLocaleString('en-US')} characters`}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving || !valid || unchanged}>
                    {channel == null ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** Create (category == null) or edit a category: code (fixed once created), names per language, order, active. */
function CategoryEditDialog({ open, category, onClose, onSaved }: {
    open: boolean;
    category: BetupFeedChannelCategoryDto | null;
    onClose: () => void;
    onSaved: (saved: BetupFeedChannelCategoryDto | null) => void;
}) {
    const [code, setCode] = React.useState('');
    const [names, setNames] = React.useState<TextMap>(textMapOf(null));
    const [sortOrder, setSortOrder] = React.useState('0');
    const [active, setActive] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setCode(category?.code ?? '');
        setNames(textMapOf(category?.names));
        setSortOrder(String(category?.sort_order ?? 0));
        setActive(category?.active ?? true);
        setError(null);
        setSaving(false);
    }, [open, category]);

    const codeValue = code.trim().toUpperCase();
    const codeError = category == null && !/^[A-Z0-9_]{1,32}$/.test(codeValue) ? 'Letters, digits and _ (≤ 32)' : null;
    const order = /^-?\d+$/.test(sortOrder.trim()) ? Number(sortOrder.trim()) : NaN;
    const valid = codeError == null && Number.isInteger(order) && names.en.trim().length > 0
        && BETUP_FEED_CHANNEL_LANGUAGES.every(lang => names[lang].trim().length <= CATEGORY_NAME_MAX);

    const save = async () => {
        const body: BetupFeedChannelCategoryRequestDto = { names: textsBody(names), sort_order: order, active };
        if (category == null) body.code = codeValue;
        setSaving(true);
        setError(null);
        const result = category == null
            ? await createBetupFeedChannelCategory(body)
            : await updateBetupFeedChannelCategory(category.id, body);
        setSaving(false);
        if (result.error != null) {
            setError(result.error);
            return;
        }
        onSaved(result.data);
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{category == null ? 'New category' : `Edit category ${category.code}`}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    <Stack direction="row" gap={2}>
                        <TextField
                            label="Code" required={category == null} value={category == null ? code : category.code}
                            disabled={category != null}
                            onChange={e => setCode(e.target.value.toUpperCase())}
                            inputProps={{ maxLength: CATEGORY_CODE_MAX }}
                            error={code.length > 0 && codeError != null}
                            helperText={category == null ? (codeError ?? 'e.g. ESPORTS — fixed once created (apps filter by it).') : 'Fixed (apps filter by it).'}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Sort order" value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                            inputProps={{ inputMode: 'numeric' }}
                            error={!Number.isInteger(order)}
                            helperText="Lower first."
                            sx={{ width: 140 }}
                        />
                    </Stack>
                    <FormControlLabel control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />}
                        label="Active (inactive categories are not shown to players)" />
                    <LocalizedTextsEditor fields={[{
                        label: 'Name', max: CATEGORY_NAME_MAX, values: names,
                        onChange: (lang, value) => setNames(n => ({ ...n, [lang]: value })),
                    }]} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving || !valid}>{category == null ? 'Create' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

/** The channel categories: list (sort order), create, edit. */
export function ChannelCategoriesDialog({ open, categories, loadError, onClose, onChanged }: {
    open: boolean;
    categories: BetupFeedChannelCategoryDto[];
    loadError: string | null;
    onClose: () => void;
    onChanged: () => Promise<void> | void;
}) {
    const [editing, setEditing] = React.useState<{ category: BetupFeedChannelCategoryDto | null } | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);

    React.useEffect(() => { if (open) setNotice(null); }, [open]);

    const columns: GridColDef<BetupFeedChannelCategoryDto>[] = [
        { field: 'sort_order', headerName: 'Order', type: 'number', width: 80 },
        { field: 'code', headerName: 'Code', width: 160 },
        {
            field: 'names', headerName: 'Name (en)', flex: 1, minWidth: 180, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{row.names?.en || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{languagesLabel(namedLanguagesCount(row.names))}</Typography>
                </Stack>
            ),
        },
        {
            field: 'active', headerName: 'Active', width: 100,
            renderCell: ({ row }) => row.active
                ? <Chip size="small" color="success" label="Active" />
                : <Chip size="small" variant="outlined" label="Inactive" />,
        },
        {
            field: 'actions', headerName: '', width: 90, sortable: false,
            renderCell: ({ row }) => <Button size="small" variant="outlined" onClick={() => setEditing({ category: row })}>Edit</Button>,
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" gap={2}>
                    <Box sx={{ flex: 1 }}>Channel categories</Box>
                    <Button variant="contained" onClick={() => setEditing({ category: null })}>New category</Button>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <DialogContentText variant="body2">
                        Badges on the channel screen, in this order; players can filter the channel list by category.
                        Assign them in a channel's Edit dialog.
                    </DialogContentText>
                    {loadError && <Alert severity="error">{loadError}</Alert>}
                    {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
                    <DataGrid
                        autoHeight
                        rows={categories}
                        columns={columns}
                        getRowHeight={() => 'auto'}
                        initialState={{ sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] } }}
                        hideFooter={categories.length <= 100}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        sx={{ '& .MuiDataGrid-cell': { py: 1, alignItems: 'center' } }}
                        localeText={{ noRowsLabel: 'No categories' }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
            <CategoryEditDialog
                open={editing != null}
                category={editing?.category ?? null}
                onClose={() => setEditing(null)}
                onSaved={async saved => {
                    const created = editing?.category == null;
                    setEditing(null);
                    setNotice(`${saved?.code ?? 'Category'} ${created ? 'created' : 'saved'}.`);
                    await onChanged();
                }}
            />
        </Dialog>
    );
}

type LinkKind = 'none' | 'match' | 'team';

interface PostForm {
    texts: TextMap;
    imageUrl: string;
    linkKind: LinkKind;
    linkId: string;
    userId: string;
    draft: boolean;
}

const postFormOf = (post: BetupFeedChannelPostDto | null): PostForm => ({
    texts: textMapOf(postTexts(post)),
    imageUrl: post?.image_url ?? '',
    linkKind: post?.match != null ? 'match' : post?.team != null ? 'team' : 'none',
    linkId: String(post?.match?.id ?? post?.team?.id ?? ''),
    userId: post?.user != null ? String(post.user.id) : '',
    // Agents' posts are reviewed first: a new post is a draft unless unticked.
    draft: post?.draft ?? true,
});

/** The parsed link / player ids of a form (NaN when invalid, null when none). */
const postLinks = (form: PostForm) => {
    const linkId = parsePositiveId(form.linkId);
    return {
        matchId: form.linkKind === 'match' ? linkId : null,
        teamId: form.linkKind === 'team' ? linkId : null,
        userId: form.userId.trim() ? parsePositiveId(form.userId) : null,
    };
};

/** Create: the filled texts of the channel's languages (trimmed), the image, the link, the player and `draft`. */
function postCreateBody(form: PostForm, languages: BetupFeedChannelLanguage[]): BetupFeedChannelPostRequestDto {
    const texts: Partial<Record<BetupFeedChannelLanguage, string>> = {};
    languages.forEach(lang => {
        const text = form.texts[lang].trim();
        if (text) texts[lang] = text;
    });
    const { matchId, teamId, userId } = postLinks(form);
    const body: BetupFeedChannelPostRequestDto = { texts, draft: form.draft };
    if (form.imageUrl.trim()) body.image_url = form.imageUrl.trim();
    if (matchId != null) body.match_id = matchId;
    if (teamId != null) body.team_id = teamId;
    if (userId != null) body.user_id = userId;
    return body;
}

/**
 * Edit: only what changed (docs/feed-channels.md §4 "Post PUT"). `texts` holds only the changed languages of the
 * channel — a cleared one is `null` (removes it); languages left alone (and those outside the channel's) are omitted.
 * `image_url` `""` clears the image; a removed match / team / player link is sent as `0`.
 */
function postUpdateBody(form: PostForm, post: BetupFeedChannelPostDto, languages: BetupFeedChannelLanguage[]) {
    const body: BetupFeedChannelPostUpdateRequestDto = {};
    const original = postFormOf(post);
    const texts: Partial<Record<BetupFeedChannelLanguage, string | null>> = {};
    languages.forEach(lang => {
        const now = form.texts[lang].trim();
        if (now !== original.texts[lang].trim()) texts[lang] = now || null;
    });
    if (Object.keys(texts).length > 0) body.texts = texts;
    if (form.imageUrl.trim() !== original.imageUrl.trim()) body.image_url = form.imageUrl.trim();
    const now = postLinks(form);
    const before = postLinks(original);
    if (now.matchId !== before.matchId) body.match_id = now.matchId ?? 0;
    if (now.teamId !== before.teamId) body.team_id = now.teamId ?? 0;
    if (now.userId !== before.userId) body.user_id = now.userId ?? 0;
    return body;
}

/** Tabs for the channel's languages, one text each; the tab shows ✓ when filled and turns red when too long. */
function PostTextsEditor({ languages, texts, onChange }: {
    languages: BetupFeedChannelLanguage[];
    texts: TextMap;
    onChange: (lang: BetupFeedChannelLanguage, value: string) => void;
}) {
    const [tab, setTab] = React.useState<BetupFeedChannelLanguage>(languages[0]);
    const lang = languages.includes(tab) ? tab : languages[0];
    const value = texts[lang];
    const length = value.trim().length;
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Tabs value={lang} onChange={(_, next) => setTab(next)} variant="scrollable" scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {languages.map(l => {
                    const filled = texts[l].trim().length > 0;
                    return (
                        <Tab
                            key={l}
                            value={l}
                            title={LANGUAGE_LABELS[l]}
                            sx={{
                                minWidth: 56, px: 1.5,
                                color: texts[l].trim().length > POST_TEXT_MAX ? 'error.main' : filled ? 'text.primary' : 'text.disabled',
                            }}
                            label={`${l.toUpperCase()}${filled ? ' ✓' : ''}`}
                        />
                    );
                })}
            </Tabs>
            <Stack gap={1} sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {LANGUAGE_LABELS[lang]} — players reading in {LANGUAGE_LABELS[lang]} see this text; without it they
                    see English, else any language the post has.
                </Typography>
                <TextField
                    label={`Text (${lang})`}
                    fullWidth
                    multiline
                    minRows={5}
                    maxRows={16}
                    value={value}
                    onChange={e => onChange(lang, e.target.value)}
                    inputProps={{ maxLength: POST_TEXT_MAX, dir: lang === 'ar' ? 'rtl' : undefined }}
                    error={length > POST_TEXT_MAX}
                    helperText={`${length.toLocaleString('en-US')} / ${POST_TEXT_MAX.toLocaleString('en-US')}`}
                />
            </Stack>
        </Box>
    );
}

/**
 * Write (post == null) or edit an `AGENT_POST`: a text per channel language, optional image, optional match or team
 * link and player. A new post is a draft by default.
 */
export function ChannelPostDialog({ open, channel, post, onClose, onSaved, onEditChannel }: {
    open: boolean;
    channel: BetupFeedChannelDto;
    post: BetupFeedChannelPostDto | null;
    onClose: () => void;
    onSaved: (saved: BetupFeedChannelPostDto | null, created: boolean, draft: boolean) => void;
    /** Opens the channel's Edit dialog (to set its languages). */
    onEditChannel: () => void;
}) {
    const [form, setForm] = React.useState<PostForm>(postFormOf(post));
    const [error, setError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setForm(postFormOf(post));
        setError(null);
        setSaving(false);
    }, [open, post]);

    const set = <K extends keyof PostForm>(key: K, value: PostForm[K]) => setForm(f => ({ ...f, [key]: value }));
    const languages = channelLanguages(channel);
    // Texts in languages the channel no longer writes in: not editable here, kept as they are.
    const otherLanguages = post == null ? [] : postLanguages(post).filter(lang => !languages.includes(lang));
    const filledCount = languages.filter(lang => form.texts[lang].trim().length > 0).length + otherLanguages.length;
    const textsValid = filledCount > 0 && languages.every(lang => form.texts[lang].trim().length <= POST_TEXT_MAX);

    const image = form.imageUrl.trim();
    const imageError = image && !isHttpUrl(image) ? 'An http(s) URL' : null;
    const links = postLinks(form);
    const linkError = form.linkKind !== 'none' && !((links.matchId ?? links.teamId ?? NaN) > 0) ? 'A positive id' : null;
    const userError = links.userId != null && !(links.userId > 0) ? 'A positive user id' : null;
    const changes = post == null ? null : postUpdateBody(form, post, languages);
    const unchanged = changes != null && Object.keys(changes).length === 0;
    const valid = languages.length > 0 && textsValid && imageError == null && linkError == null && userError == null;
    const draft = post == null ? form.draft : post.draft === true;

    const save = async () => {
        setSaving(true);
        setError(null);
        const result = post == null
            ? await createBetupFeedChannelPost(channel.id, postCreateBody(form, languages))
            : await updateBetupFeedChannelPost(channel.id, post.id, changes ?? {});
        setSaving(false);
        if (result.error != null) {
            setError(result.error);
            return;
        }
        onSaved(result.data, post == null, draft);
    };

    const audience = `subscribers see it in “Channels you subscribed to”${channel.is_top
        ? ', and everyone in “Most popular channels” (this channel is top)' : ''}`;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {post == null ? `New post in ${channelName(channel)}` : `Edit post #${post.id} — ${channelName(channel)}`}
                {post?.draft && <Chip size="small" color="warning" label="Draft" sx={{ ml: 1 }} />}
            </DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    {languages.length === 0 ? (
                        <Alert
                            severity="warning"
                            action={<Button color="inherit" size="small" onClick={onEditChannel}>Edit channel</Button>}
                        >
                            This channel has no languages yet. A post has a text per channel language, so set the
                            channel's Languages first (Edit channel), then come back to write the post.
                        </Alert>
                    ) : (
                        <>
                            <DialogContentText variant="body2">
                                {post == null
                                    ? (form.draft
                                        ? 'Saved as a draft: players don\'t see it anywhere until you press Publish in the posts list.'
                                        : `Published right away at the top of the feed: ${audience}.`)
                                    : post.draft
                                        ? 'A draft: saving keeps it a draft (publish it from the posts list).'
                                        : 'Edited in place: its time in the feed, likes and comments stay; there is no “edited” mark.'}
                                {!channel.active && ' This channel is inactive, so its posts stay out of the feeds until it is activated.'}
                            </DialogContentText>
                            <PostTextsEditor
                                languages={languages}
                                texts={form.texts}
                                onChange={(lang, value) => setForm(f => ({ ...f, texts: { ...f.texts, [lang]: value } }))}
                            />
                            <Typography variant="caption" color={filledCount === 0 ? 'error' : 'text.secondary'} sx={{ mt: -1 }}>
                                {filledCount === 0
                                    ? 'Write the text in at least one language.'
                                    : `Write every language of the channel (${languages.map(l => l.toUpperCase()).join(', ')}) where you can.`
                                    + (post != null ? ' Clearing a language removes its text from the post.' : '')}
                            </Typography>
                            {otherLanguages.length > 0 && (
                                <Alert severity="info" variant="outlined">
                                    The post also has text in {otherLanguages.map(l => LANGUAGE_LABELS[l]).join(', ')}, which
                                    the channel no longer writes in — kept as it is.
                                </Alert>
                            )}
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
                                <TextField
                                    label="Image URL (optional)" fullWidth value={form.imageUrl}
                                    onChange={e => set('imageUrl', e.target.value)}
                                    inputProps={{ maxLength: CHANNEL_LOGO_MAX }}
                                    error={imageError != null}
                                    helperText={imageError ?? (post?.image_url ? 'Clear it to remove the image.' : ' ')}
                                />
                                <Box sx={{ width: { xs: '100%', sm: 240 }, flexShrink: 0 }}><ImagePreview url={image} /></Box>
                            </Stack>
                            <Stack>
                                <FormLabel>Link</FormLabel>
                                <RadioGroup row value={form.linkKind}
                                    onChange={e => setForm(f => ({ ...f, linkKind: e.target.value as LinkKind, linkId: '' }))}>
                                    <FormControlLabel value="none" control={<Radio />} label="None" />
                                    <FormControlLabel value="match" control={<Radio />} label="Match" />
                                    <FormControlLabel value="team" control={<Radio />} label="Team" />
                                </RadioGroup>
                                {form.linkKind !== 'none' && (
                                    <TextField
                                        label={form.linkKind === 'match' ? 'Match id' : 'Team id'} required value={form.linkId}
                                        onChange={e => set('linkId', e.target.value)}
                                        inputProps={{ inputMode: 'numeric' }}
                                        error={form.linkId.length > 0 && linkError != null}
                                        helperText={form.linkKind === 'match'
                                            ? 'Players see the match strip under the post.'
                                            : 'Players see the team chip under the post.'}
                                        sx={{ maxWidth: 240 }}
                                    />
                                )}
                            </Stack>
                            <TextField
                                label="Player (user id, optional)" value={form.userId}
                                onChange={e => set('userId', e.target.value)}
                                inputProps={{ inputMode: 'numeric' }}
                                error={userError != null}
                                helperText={userError ?? 'Names a player (e.g. to congratulate them): the card shows them. Allowed with a match or team link.'}
                                sx={{ maxWidth: 420 }}
                            />
                            {post == null && (
                                <Stack>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.draft} onChange={e => set('draft', e.target.checked)} />}
                                        label="Save as draft (not visible to players until published)"
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                                        On by default: channel posts are reviewed as drafts first. Untick to publish right
                                        away.
                                    </Typography>
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    color={post == null && !form.draft ? 'success' : 'primary'}
                    onClick={save}
                    disabled={saving || !valid || unchanged}
                >
                    {post != null ? 'Save' : form.draft ? 'Save draft' : 'Publish now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
